/**
 * Helper functions for handling asset paths
 */
import placeholderIcons from '../assets/images/placeholder';

// Add type definition for Vite's import.meta.env
interface ImportMetaEnv {
  PROD: boolean;
  DEV: boolean;
  BASE_URL: string;
  MODE: string;
}

interface ImportMeta {
  env: ImportMetaEnv;
}

/**
 * Get the correct asset path - this is a utility function to handle differences
 * between development and production asset paths, and provide placeholders when
 * assets are not available
 */
export const getAssetPath = (path: string): string => {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  
  try {
    // Detect current environment and adjust paths accordingly
    const mode = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.MODE : 'development';
    
    // Debugging
    //console.log(`Asset mode: ${mode}, Path: ${cleanPath}`);
    
    // This is critical: determining the right base for different environments
    // For 'serve' and 'production', we need the base URL
    const baseUrl = '/';
    
    // For images that exist in our assets folder, return the correct path
    if (cleanPath.includes('@1x') || cleanPath.includes('@2x')) {
      return `${baseUrl}${cleanPath}`;
    }
    
    // Handle special cases for images that might have different paths
    if (cleanPath === 'assets/images/avatar-2.png') {
      return `${baseUrl}assets/images/avatar-2.png`;
    } else if (cleanPath === 'assets/images/phan-icon.png') {
      return `${baseUrl}assets/images/phan-icon@1x.png`;
    } else if (cleanPath === 'assets/images/valor-points.png') {
      return `${baseUrl}assets/images/valor-points@1x.png`;
    } else if (cleanPath === 'assets/images/offer-icon.png') {
      return `${baseUrl}assets/images/offer-icon@1x.png`;
    } else if (cleanPath === 'assets/images/community-hub-banner.png') {
      return `${baseUrl}assets/images/community-hub-banner@1x.png`;
    } else if (cleanPath === 'assets/images/achievements-unlocked.png') {
      return `${baseUrl}assets/images/achievements-unlocked@1x.png`;
    } else if (cleanPath === 'assets/images/achievements.png') {
      return `${baseUrl}assets/images/achievements@1x.png`;
    } else if (cleanPath === 'assets/images/achievements-bg.png') {
      return `${baseUrl}assets/images/achievements-bg@1x.png`;
    }
    
    // For video files, return the path directly
    if (cleanPath.endsWith('.mp4')) {
      return `${baseUrl}${cleanPath}`;
    }
    
    // For SVG files, return the path directly
    if (cleanPath.endsWith('.svg')) {
      return `${baseUrl}${cleanPath}`;
    }
    
    // For other image files, try to return the path directly
    if (cleanPath.endsWith('.png') || cleanPath.endsWith('.jpg') || cleanPath.endsWith('.jpeg') || cleanPath.endsWith('.gif')) {
      return `${baseUrl}${cleanPath}`;
    }
    
    // For fonts
    if (cleanPath.includes('assets/fonts/') || 
        cleanPath.endsWith('.woff') || 
        cleanPath.endsWith('.woff2') || 
        cleanPath.endsWith('.ttf') || 
        cleanPath.endsWith('.eot') || 
        cleanPath.endsWith('.otf')) {
      return `${baseUrl}${cleanPath}`;
    }
    
    // Try to dynamically import the asset
    // Default to a placeholder image if the asset doesn't exist
    if (cleanPath === 'assets/images/dashboard-icon-1.svg') {
      return placeholderIcons.dashboardIcon1;
    } else if (cleanPath === 'assets/images/dashboard-icon-2.svg') {
      return placeholderIcons.dashboardIcon2;
    } else if (cleanPath === 'assets/images/dashboard-icon-3.svg') {
      return placeholderIcons.dashboardIcon3;
    } else if (cleanPath === 'assets/images/transaction-icon-1.svg') {
      return placeholderIcons.transactionIcon1;
    } else if (cleanPath === 'assets/images/transaction-icon-2.svg') {
      return placeholderIcons.transactionIcon2;
    } else if (cleanPath === 'assets/images/transaction-icon-3.svg') {
      return placeholderIcons.transactionIcon3;
    } else if (cleanPath === 'assets/images/icon-deposit.svg') {
      return placeholderIcons.actionDeposit;
    } else if (cleanPath === 'assets/images/icon-withdraw.svg') {
      return placeholderIcons.actionWithdraw;
    } else if (cleanPath === 'assets/images/icon-swap.svg') {
      return placeholderIcons.actionSwap;
    } else if (cleanPath === 'assets/images/icon-stake.svg') {
      return placeholderIcons.actionStake;
    } else if (cleanPath === 'assets/images/logo.svg') {
      return placeholderIcons.logo;
    } else if (cleanPath === 'assets/images/profile-example.jpg') {
      return placeholderIcons.profile;
    } else if (cleanPath.startsWith('assets/images/header-')) {
      // For header icons, use a default
      return placeholderIcons.dashboardIcon1;
    }
    
    // Default case - return as is and hope it works
    return `${baseUrl}${cleanPath}`;
  } catch (error) {
    console.error(`Failed to load asset: ${cleanPath}`, error);
    return placeholderIcons.dashboardIcon1; // Default placeholder
  }
};

/**
 * Icons for the application - centralizing these makes it easier to manage
 */
export const iconPaths = {
  dashboard: 'assets/images/dashboard-icon-1.svg',
  wallet: 'assets/images/dashboard-icon-2.svg',
  battle: 'assets/images/dashboard-icon-3.svg',
  transaction: {
    purchase: 'assets/images/transaction-icon-1.svg',
    reward: 'assets/images/transaction-icon-2.svg',
    withdrawal: 'assets/images/transaction-icon-3.svg'
  },
  actions: {
    deposit: 'assets/images/icon-deposit.svg',
    withdraw: 'assets/images/icon-withdraw.svg',
    swap: 'assets/images/icon-swap.svg',
    stake: 'assets/images/icon-stake.svg'
  }
};

export default {
  getAssetPath,
  iconPaths
}; 